import { useRef } from 'react';
import { DefaultError, MutationFunction, useMutation, UseMutationOptions } from '@tanstack/react-query';

// The client-side per-action throttle window. Exported as the single source of
// truth so the recording/transcription HOCs and the STOP_GRACE_MS invariant
// (useAutoCallFeature) all reference the same value.
export const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute

// The limiter reads `action` (its key) and `force` (its bypass) off the
// variables, so callers have to supply them. Constrained rather than cast, so
// the compiler rejects a caller that would otherwise silently share one key.
export interface RateLimitedVariables {
  action: string;
  force?: boolean;
}

export default function useRateLimitedMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables extends RateLimitedVariables = RateLimitedVariables,
  TContext = unknown,
>({ mutationFn, ...options }: UseMutationOptions<TData, TError, TVariables, TContext>, ms: number) {
  // Keyed by action so the limiter is PER action: a stop must never block a
  // subsequent start. Otherwise a drop+rejoin around the stop-grace boundary
  // (the stop fires, then the rejoin's start lands < ms later) would be rate-
  // limited away, silently leaving recording off for the rest of the meeting.
  const lastCalledRef = useRef<Record<string, number>>({});

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: async (params) => {
      const now = Date.now();

      // A forced call bypasses the rate limit. Used for the deliberate meeting-end
      // stop so a clean stop is always sent even if a start happened < ms ago.
      const lastCalled = lastCalledRef.current[params.action];

      if (!params.force && lastCalled !== undefined && now - lastCalled < ms) {
        throw new Error('Rate limit exceeded');
      }

      lastCalledRef.current[params.action] = now; // Update the last called time for this action

      return (mutationFn as MutationFunction<TData, TVariables>)(params);
    }
  });
}
